// src/api/laboratory-api.js
import { gql } from '@apollo/client';
import client from './apollo-client';

// ================================
// LAB SCAN STATS QUERY
// ================================
export const GET_LAB_SCAN_STATS = gql`
  query GetLabScanStats($laboratoryId: ID!) {
    labScanStats(laboratoryId: $laboratoryId) {
      totalScanned
      topProducts {
        productId
        productName
        scanCount
      }
    }
  }
`;

// ================================
// LABORATORY QUERIES
// ================================

// Get all laboratories
export const GET_LABORATORIES = gql`
  query GetLaboratories {
    laboratories {
      id
      nom
      raisonSociale
      adresse
      registre
      ice
      job
      userId
      staff
      commissionFund
      commissionUsed
      soldeRestant
      createdAt
      updatedAt
      owner {
        id
        email
        firstName
        lastName
        tel
        userType
        isActive
        lastLogin
      }
      staffUsers {
        id
        email
        firstName
        lastName
        tel
        userType
        isActive
        lastLogin
      }
      users {
        id
        email
        firstName
        lastName
        tel
        userType
        isActive
        lastLogin
      }
      products {
        id
        name
        price
        description
        image
        category
        subcategory
        createdAt
        updatedAt
      }
      promotions {
        id
        name
        status
        commissionType
        commissionAmount
        commissionPercentage
        startDate
        endDate
        createdAt
        updatedAt
      }
      contrats {
        id
        number
        nom
        dateStart
        dateEnd
        createdAt
        updatedAt
      }
    }
  }
`;

// Get laboratory by ID
export const GET_LABORATORY = gql`
  query GetLaboratory($id: ID!) {
    laboratory(id: $id) {
      id
      nom
      raisonSociale
      adresse
      registre
      ice
      job
      userId
      staff
      commissionFund
      commissionUsed
      createdAt
      updatedAt
      owner {
        id
        email
        firstName
        lastName
        tel
        userType
        isActive
        lastLogin
      }
      staffUsers {
        id
        email
        firstName
        lastName
        tel
        userType
        isActive
        lastLogin
      }
      users {
        id
        email
        firstName
        lastName
        tel
        userType
        isActive
        lastLogin
      }
      products {
        id
        name
        price
        description
        image
        category
        subcategory
        images
        imagethree
        quatriemimage
        cinquiemimage
        video
        videoLink
        createdAt
        updatedAt
        promotions {
          id
          name
          status
          commissionAmount
          commissionPercentage
          startDate
          endDate
        }
      }
      promotions {
        id
        name
        startDate
        endDate
        status
        commissionType
        commissionAmount
        commissionPercentage
        quantity
        products {
          id
          name
          price
        }
        createdAt
        updatedAt
      }
      contrats {
        id
        number
        nom
        dateStart
        dateEnd
        createdAt
        updatedAt
      }
    }
  }
`;

// Get laboratory by user ID
export const GET_LABORATORY_BY_USER = gql`
  query GetLaboratoryByUser($userId: ID!) {
    laboratoryByUserId(userId: $userId) {
      id
      nom
      raisonSociale
      adresse
      registre
      ice
      job
      userId
      staff
      commissionFund
      commissionUsed
      soldeRestant
      createdAt
      updatedAt
      owner {
        id
        email
        firstName
        lastName
        tel
      }
      staffUsers {
        id
        email
        firstName
        lastName
        tel
      }
      users {
        id
        email
        firstName
        lastName
        tel
      }
      products {
        id
        name
        price
        description
        image
        category
        subcategory
        createdAt
        updatedAt
      }
      promotions {
        id
        name
        status
        startDate
        endDate
        commissionType
        commissionAmount
        commissionPercentage
      }
      contrats {
        id
        number
        nom
        dateStart
        dateEnd
      }
    }
  }
`;

// ================================
// PRODUCT QUERIES (by Laboratory)
// ================================

// Get products by laboratory
export const GET_PRODUCTS_BY_LABORATORY = gql`
  query GetProductsByLaboratory($laboratoryId: ID!) {
    productsByLaboratory(laboratoryId: $laboratoryId) {
      id
      name
      description
      price
      prixPGHT
      category
      subcategory
      image
      images
      imagethree
      quatriemimage
      cinquiemimage
      video
      videoLink
      laboratoryId
      laboratory {
        id
        nom
      }
      promotions {
        id
        name
        status
        commissionAmount
        commissionPercentage
        startDate
        endDate
      }
      createdAt
      updatedAt
    }
  }
`;

// ================================
// PROMOTION QUERIES (by Laboratory)
// ================================

// Get all promotions (admin)
export const GET_ALL_PROMOTIONS = gql`
  query GetAllPromotions {
    allPromotions {
      id
      name
      startDate
      endDate
      quantity
      labelsCount
      draftLabelsCount
      status
      commissionType
      commissionAmount
      commissionPercentage
      pdfFile
      contratId
      isSponsored
      allowedUserTypes
      laboratory {
        id
        nom
      }
      products {
        id
        name
        price
        prixPGHT
        image
        images
        imagethree
        quatriemimage
        cinquiemimage
        videoLink
        video
        description
        category
        subcategory
        laboratoryId
      }
      contrat {
        id
        number
        nom
      }
      createdAt
      updatedAt
    }
  }
`;

// Get promotions by laboratory
export const GET_PROMOTIONS_BY_LABORATORY = gql`
  query GetPromotionsByLaboratory($laboratoryId: ID!) {
    promotionsByLaboratory(laboratoryId: $laboratoryId) {
      id
      name
      startDate
      endDate
      quantity
      status
      commissionType
      commissionPercentage
      commissionAmount
      pdfFile
      contratId
      laboratory {
        id
        nom
      }
      products {
        id
        name
        price
        image
      }
      contrat {
        id
        number
        nom
      }
      createdAt
      updatedAt
    }
  }
`;

// ================================
// CONTRACT QUERIES (by Laboratory)
// ================================

// Get contracts by laboratory
export const GET_CONTRACTS_BY_LABORATORY = gql`
  query GetContractsByLaboratory($laboratoryId: ID!) {
    contractsByLaboratory(laboratoryId: $laboratoryId) {
      id
      number
      nom
      dateStart
      dateEnd
      piecesJointes
      laboratoryId
      laboratory {
        id
        nom
      }
      promotions {
        id
        name
        status
      }
      createdAt
      updatedAt
    }
  }
`;

// ================================
// ADD QUERIES
// ================================

// Get adds by laboratory
export const GET_ADDS_BY_LABORATORY = gql`
  query GetAddsByLaboratory($laboratoryId: ID!) {
    addsByLaboratory(laboratoryId: $laboratoryId) {
      id
      dateDebut
      dateFin
      laboratoryId
      productId
      laboratory {
        id
        nom
      }
      product {
        id
        name
        price
        image
      }
      createdAt
      updatedAt
    }
  }
`;

// ================================
// USER QUERIES
// ================================

// Get users by type
export const GET_USERS_BY_TYPE = gql`
  query GetUsersByType($userType: String!) {
    usersByType(userType: $userType) {
      id
      email
      firstName
      lastName
      tel
      userType
      isActive
      lastLogin
      createdAt
      updatedAt
    }
  }
`;

// ================================
// LABEL QUERIES
// ================================

// Get promotion labels
export const GET_PROMOTION_LABELS = gql`
  query GetPromotionLabels($promotionId: ID!) {
    labelsByPromotion(promotionId: $promotionId) {
      id
      code
      isUsed
      status
      image
      date
      promotionId
      productId
      pharmacyId
      userId
      promotion {
        id
        name
        status
      }
      product {
        id
        name
        price
      }
      user {
        id
        firstName
        lastName
        email
      }
      pharmacy {
        id
        namePharmacy
        region {
          id
          nomRegion
        }
        ville {
          id
          nomVilleVillage
        }
      }
      createdAt
      updatedAt
    }
  }
`;

// Get labels statistics
export const GET_LABELS_STATISTICS = gql`
  query GetLabelsStatistics($promotionId: ID!) {
    labelsStatistics(promotionId: $promotionId) {
      totalGenerated      
      totalUsed          
      totalUnused        
      usageRate          
      scannedToday       
      scannedThisWeek    
      scannedThisMonth   
      topPharmacies {    
        pharmacy {
          id
          namePharmacy
        }
        count
      }
      dailyScans {       
        date
        count
      }
    }
  }
`;

// Get labels count by promotion
export const GET_LABELS_COUNT_BY_PROMOTION = gql`
  query GetLabelsCountByPromotion($promotionId: ID!) {
    labelsCountByPromotion(promotionId: $promotionId)
  }
`;

// Get draft (EDITED) labels count by promotion
export const GET_DRAFT_LABELS_COUNT_BY_PROMOTION = gql`
  query GetDraftLabelsCountByPromotion($promotionId: ID!) {
    draftLabelsCountByPromotion(promotionId: $promotionId)
  }
`;

// ================================
// LABORATORY MUTATIONS
// ================================

// Create laboratory
export const CREATE_LABORATORY = gql`
  mutation CreateLaboratory($createLaboratoryInput: CreateLaboratoryInput!) {
    createLaboratory(createLaboratoryInput: $createLaboratoryInput) {
      id
      nom
      raisonSociale
      adresse
      registre
      ice
      job
      userId
      staff
      createdAt
      updatedAt
      owner {
        id
        email
        firstName
        lastName
      }
    }
  }
`;

// Update laboratory
export const UPDATE_LABORATORY = gql`
  mutation UpdateLaboratory($updateLaboratoryInput: UpdateLaboratoryInput!) {
    updateLaboratory(updateLaboratoryInput: $updateLaboratoryInput) {
      id
      nom
      raisonSociale
      adresse
      registre
      ice
      job
      userId
      staff
      createdAt
      updatedAt
      owner {
        id
        email
        firstName
        lastName
      }
    }
  }
`;

// Delete laboratory
export const REMOVE_LABORATORY = gql`
  mutation RemoveLaboratory($id: ID!) {
    removeLaboratory(id: $id)
  }
`;

// Add staff to laboratory
export const ADD_STAFF_TO_LABORATORY = gql`
  mutation AddStaffToLaboratory($laboratoryId: ID!, $userId: ID!) {
    addStaffToLaboratory(laboratoryId: $laboratoryId, userId: $userId) {
      id
      nom
      staffUsers {
        id
        email
        firstName
        lastName
      }
    }
  }
`;

// Remove staff from laboratory
export const REMOVE_STAFF_FROM_LABORATORY = gql`
  mutation RemoveStaffFromLaboratory($laboratoryId: ID!, $userId: ID!) {
    removeStaffFromLaboratory(laboratoryId: $laboratoryId, userId: $userId) {
      id
      nom
      staffUsers {
        id
        email
        firstName
        lastName
      }
    }
  }
`;

// ================================
// PRODUCT MUTATIONS
// ================================

// Create product
export const CREATE_PRODUCT = gql`
  mutation CreateProduct($createProductInput: CreateProductInput!) {
    createProduct(createProductInput: $createProductInput) {
      id
      name
      description
      price
      prixPGHT
      category
      subcategory
      image
      images
      imagethree
      quatriemimage
      cinquiemimage
      video
      videoLink
      laboratoryId
      laboratory {
        id
        nom
      }
      createdAt
      updatedAt
    }
  }
`;

// Update product
export const UPDATE_PRODUCT = gql`
  mutation UpdateProduct($updateProductInput: UpdateProductInput!) {
    updateProduct(updateProductInput: $updateProductInput) {
      id
      name
      description
      price
      prixPGHT
      category
      subcategory
      image
      images
      imagethree
      quatriemimage
      cinquiemimage
      video
      videoLink
      laboratoryId
      laboratory {
        id
        nom
      }
      createdAt
      updatedAt
    }
  }
`;

// Remove product
export const REMOVE_PRODUCT = gql`
  mutation RemoveProduct($id: ID!) {
    removeProduct(id: $id)
  }
`;

// ================================
// PROMOTION MUTATIONS
// ================================

// Create promotion
export const CREATE_PROMOTION = gql`
  mutation CreatePromotion($createPromotionInput: CreatePromotionInput!) {
    createPromotion(createPromotionInput: $createPromotionInput) {
      id
      name
      startDate
      endDate
      quantity
      status
      commissionType
      commissionPercentage
      commissionAmount
      pdfFile
      laboratory {
        id
        nom
      }
      products {
        id
        name
        price
      }
      createdAt
      updatedAt
    }
  }
`;

// Update promotion
export const UPDATE_PROMOTION = gql`
  mutation UpdatePromotion($updatePromotionInput: UpdatePromotionInput!) {
    updatePromotion(updatePromotionInput: $updatePromotionInput) {
      id
      name
      startDate
      endDate
      quantity
      status
      commissionType
      commissionPercentage
      commissionAmount
      pdfFile
      laboratory {
        id
        nom
      }
      products {
        id
        name
        price
      }
      createdAt
      updatedAt
    }
  }
`;

// Remove promotion
export const REMOVE_PROMOTION = gql`
  mutation RemovePromotion($id: ID!) {
    removePromotion(id: $id)
  }
`;

// ================================
// CONTRACT MUTATIONS
// ================================

// Create contract
export const CREATE_CONTRACT = gql`
  mutation CreateContract($createContractInput: CreateContractInput!) {
    createContract(createContractInput: $createContractInput) {
      id
      number
      nom
      dateStart
      dateEnd
      laboratoryId
      laboratory {
        id
        nom
      }
      createdAt
      updatedAt
    }
  }
`;

// Update contract
export const UPDATE_CONTRACT = gql`
  mutation UpdateContract($updateContractInput: UpdateContractInput!) {
    updateContract(updateContractInput: $updateContractInput) {
      id
      number
      nom
      dateStart
      dateEnd
      laboratoryId
      laboratory {
        id
        nom
      }
      createdAt
      updatedAt
    }
  }
`;

// Delete contract
export const DELETE_CONTRACT = gql`
  mutation DeleteContract($id: ID!) {
    removeContract(id: $id)
  }
`;

// ================================
// ADD MUTATIONS
// ================================

// Create add
export const CREATE_ADD = gql`
  mutation CreateAdd($createAddInput: CreateAddInput!) {
    createAdd(createAddInput: $createAddInput) {
      id
      dateDebut
      dateFin
      laboratoryId
      productId
      laboratory {
        id
        nom
      }
      product {
        id
        name
        price
      }
      createdAt
      updatedAt
    }
  }
`;

// Update add
export const UPDATE_ADD = gql`
  mutation UpdateAdd($updateAddInput: UpdateAddInput!) {
    updateAdd(updateAddInput: $updateAddInput) {
      id
      dateDebut
      dateFin
      laboratoryId
      productId
      laboratory {
        id
        nom
      }
      product {
        id
        name
        price
      }
      createdAt
      updatedAt
    }
  }
`;

// Delete add
export const DELETE_ADD = gql`
  mutation DeleteAdd($id: ID!) {
    removeAdd(id: $id)
  }
`;

// ================================
// USER MUTATIONS
// ================================

// Create user
export const CREATE_USER = gql`
  mutation CreateUser($email: String!, $password: String!, $userType: String!, $firstName: String, $lastName: String, $tel: String) {
    createUser(email: $email, password: $password, userType: $userType, firstName: $firstName, lastName: $lastName, tel: $tel) {
      id
      email
      firstName
      lastName
      tel
      userType
      isActive
      isPhoneVerified
      createdAt
      updatedAt
    }
  }
`;

// Update user
export const UPDATE_USER = gql`
  mutation UpdateUser($updateUserInput: UpdateUserInput!) {
    updateUser(updateUserInput: $updateUserInput) {
      id
      email
      firstName
      lastName
      tel
      userType
      isActive
      createdAt
      updatedAt
    }
  }
`;

// Delete user
export const DELETE_USER = gql`
  mutation DeleteUser($id: ID!) {
    removeUser(id: $id)
  }
`;

// Associate user with laboratory
export const ASSOCIATE_USER_WITH_LABORATORY = gql`
  mutation AssociateUserWithLaboratory($userId: ID!, $laboratoryId: ID!) {
    associateUserToLaboratory(userId: $userId, laboratoryId: $laboratoryId)
  }
`;

// ================================
// ADDITIONAL DELETE MUTATIONS (Aliases)
// ================================

// Add commission fund to laboratory
export const ADD_LAB_COMMISSION_FUND = gql`
  mutation AddLabCommissionFund($laboratoryId: ID!, $amount: Float!, $description: String, $receivedAt: DateTime) {
    addLabCommissionFund(laboratoryId: $laboratoryId, amount: $amount, description: $description, receivedAt: $receivedAt) {
      id
      commissionFund
      commissionUsed
    }
  }
`;

// Delete laboratory (alias for remove)
export const DELETE_LABORATORY = gql`
  mutation DeleteLaboratory($id: ID!) {
    removeLaboratory(id: $id)
  }
`;

// Delete product (alias for remove)
export const DELETE_PRODUCT = gql`
  mutation DeleteProduct($id: ID!) {
    removeProduct(id: $id)
  }
`;

// Delete promotion (alias for remove)
export const DELETE_PROMOTION = gql`
  mutation DeletePromotion($id: ID!) {
    removePromotion(id: $id)
  }
`;

// Remove staff member (alias)
export const REMOVE_STAFF_MEMBER = gql`
  mutation RemoveStaffMember($laboratoryId: ID!, $userId: ID!) {
    removeStaffFromLaboratory(laboratoryId: $laboratoryId, userId: $userId) {
      id
      nom
      staffUsers {
        id
        email
        firstName
        lastName
      }
    }
  }
`;

// ================================
// LABEL/QR CODE MUTATIONS
// ================================

// Confirm labels (EDITED → NOT_SCANNED) for a promotion
export const CONFIRM_LABELS = gql`
  mutation ConfirmLabels($promotionId: ID!) {
    confirmLabels(promotionId: $promotionId) {
      success
      count
      message
    }
  }
`;

// Delete draft (EDITED) labels for a promotion
export const DELETE_DRAFT_LABELS = gql`
  mutation DeleteDraftLabels($promotionId: ID!) {
    deleteDraftLabels(promotionId: $promotionId) {
      success
      count
      message
    }
  }
`;

// Generate Excel for draft (EDITED) labels only — no new codes created
export const GENERATE_DRAFT_LABELS_EXCEL = gql`
  mutation GenerateDraftLabelsExcel($promotionId: ID!) {
    generateDraftLabelsExcel(promotionId: $promotionId) {
      success
      count
      message
      excelBase64
      excelFilename
    }
  }
`;

// Generate QR codes
export const GENERATE_QR_CODES = gql`
  mutation GenerateQRCodes($promotionId: ID!, $quantity: Int!) {
    generateQRCodes(promotionId: $promotionId, quantity: $quantity) {
      success
      count
      message
      pdfBase64
      filename
      excelBase64
      excelFilename
      labels {
        id
        code
        isUsed
        status
        image
        date
        promotionId
        productId
        pharmacyId
        userId
        createdAt
        updatedAt
      }
    }
  }
`;

// ================================
// UTILITY FUNCTIONS
// ================================

// Helper function to ensure complete product data
export const ensureCompleteProductData = (products) => {
  return products.map(product => ({
    id: product.id,
    name: product.name || '',
    description: product.description || '',
    price: product.price || 0,
    prixPGHT: product.prixPGHT ?? null,
    category: product.category || '',
    subcategory: product.subcategory || '',
    image: product.image || '',
    images: product.images || [],
    imagethree: product.imagethree || '',
    quatriemimage: product.quatriemimage || '',
    cinquiemimage: product.cinquiemimage || '',
    video: product.video || '',
    videoLink: product.videoLink || '',
    laboratoryId: product.laboratoryId || '',
    laboratory: product.laboratory || null,
    promotions: product.promotions || [],
    createdAt: product.createdAt,
    updatedAt: product.updatedAt
  }));
};

// ================================
// API FUNCTIONS
// ================================

export const laboratoryApi = {
  // Laboratory operations
  getAllLaboratories: async () => {
    try {
      const { data } = await client.query({
        query: GET_LABORATORIES,
        fetchPolicy: 'network-only'
      });
      return data.laboratories;
    } catch (error) {
      console.error('Get laboratories error:', error);
      throw error;
    }
  },

  getLaboratory: async (id) => {
    try {
      const { data } = await client.query({
        query: GET_LABORATORY,
        variables: { id },
        fetchPolicy: 'network-only'
      });
      return data.laboratory;
    } catch (error) {
      console.error(`Get laboratory ${id} error:`, error);
      throw error;
    }
  },

  getLaboratoryByUser: async (userId) => {
    try {
      const { data } = await client.query({
        query: GET_LABORATORY_BY_USER,
        variables: { userId },
        fetchPolicy: 'network-only'
      });
      return data.laboratoryByUserId;
    } catch (error) {
      console.error(`Get laboratory by user ${userId} error:`, error);
      throw error;
    }
  },

  createLaboratory: async (laboratoryData) => {
    try {
      const { data } = await client.mutate({
        mutation: CREATE_LABORATORY,
        variables: { createLaboratoryInput: laboratoryData }
      });
      return data.createLaboratory;
    } catch (error) {
      console.error('Create laboratory error:', error);
      throw error;
    }
  },

  updateLaboratory: async (laboratoryData) => {
    try {
      const { data } = await client.mutate({
        mutation: UPDATE_LABORATORY,
        variables: { updateLaboratoryInput: laboratoryData }
      });
      return data.updateLaboratory;
    } catch (error) {
      console.error(`Update laboratory ${laboratoryData.id} error:`, error);
      throw error;
    }
  },

  removeLaboratory: async (id) => {
    try {
      const { data } = await client.mutate({
        mutation: REMOVE_LABORATORY,
        variables: { id }
      });
      return data.removeLaboratory;
    } catch (error) {
      console.error(`Remove laboratory ${id} error:`, error);
      throw error;
    }
  },

  // Product operations
  getProductsByLaboratory: async (laboratoryId) => {
    try {
      const { data } = await client.query({
        query: GET_PRODUCTS_BY_LABORATORY,
        variables: { laboratoryId },
        fetchPolicy: 'network-only'
      });
      return ensureCompleteProductData(data.productsByLaboratory || []);
    } catch (error) {
      console.error(`Get products by laboratory ${laboratoryId} error:`, error);
      throw error;
    }
  },

  createProduct: async (productData) => {
    try {
      const { data } = await client.mutate({
        mutation: CREATE_PRODUCT,
        variables: { createProductInput: productData }
      });
      return data.createProduct;
    } catch (error) {
      console.error('Create product error:', error);
      throw error;
    }
  },

  updateProduct: async (productData) => {
    try {
      const { data } = await client.mutate({
        mutation: UPDATE_PRODUCT,
        variables: { updateProductInput: productData }
      });
      return data.updateProduct;
    } catch (error) {
      console.error(`Update product ${productData.id} error:`, error);
      throw error;
    }
  },

  removeProduct: async (id) => {
    try {
      const { data } = await client.mutate({
        mutation: REMOVE_PRODUCT,
        variables: { id }
      });
      return data.removeProduct;
    } catch (error) {
      console.error(`Remove product ${id} error:`, error);
      throw error;
    }
  },

  // Promotion operations
  getPromotionsByLaboratory: async (laboratoryId) => {
    try {
      const { data } = await client.query({
        query: GET_PROMOTIONS_BY_LABORATORY,
        variables: { laboratoryId },
        fetchPolicy: 'network-only'
      });
      return data.promotionsByLaboratory;
    } catch (error) {
      console.error(`Get promotions by laboratory ${laboratoryId} error:`, error);
      throw error;
    }
  },

  createPromotion: async (promotionData) => {
    try {
      const { data } = await client.mutate({
        mutation: CREATE_PROMOTION,
        variables: { createPromotionInput: promotionData }
      });
      return data.createPromotion;
    } catch (error) {
      console.error('Create promotion error:', error);
      throw error;
    }
  },

  updatePromotion: async (promotionData) => {
    try {
      const { data } = await client.mutate({
        mutation: UPDATE_PROMOTION,
        variables: { updatePromotionInput: promotionData }
      });
      return data.updatePromotion;
    } catch (error) {
      console.error(`Update promotion ${promotionData.id} error:`, error);
      throw error;
    }
  },

  removePromotion: async (id) => {
    try {
      const { data } = await client.mutate({
        mutation: REMOVE_PROMOTION,
        variables: { id }
      });
      return data.removePromotion;
    } catch (error) {
      console.error(`Remove promotion ${id} error:`, error);
      throw error;
    }
  },

  // Contract operations
  getContractsByLaboratory: async (laboratoryId) => {
    try {
      const { data } = await client.query({
        query: GET_CONTRACTS_BY_LABORATORY,
        variables: { laboratoryId },
        fetchPolicy: 'network-only'
      });
      return data.contractsByLaboratory;
    } catch (error) {
      console.error(`Get contracts by laboratory ${laboratoryId} error:`, error);
      throw error;
    }
  },

  createContract: async (contractData) => {
    try {
      const { data } = await client.mutate({
        mutation: CREATE_CONTRACT,
        variables: { createContractInput: contractData }
      });
      return data.createContract;
    } catch (error) {
      console.error('Create contract error:', error);
      throw error;
    }
  },

  updateContract: async (contractData) => {
    try {
      const { data } = await client.mutate({
        mutation: UPDATE_CONTRACT,
        variables: { updateContractInput: contractData }
      });
      return data.updateContract;
    } catch (error) {
      console.error(`Update contract ${contractData.id} error:`, error);
      throw error;
    }
  },

  removeContract: async (id) => {
    try {
      const { data } = await client.mutate({
        mutation: DELETE_CONTRACT,
        variables: { id }
      });
      return data.removeContract;
    } catch (error) {
      console.error(`Remove contract ${id} error:`, error);
      throw error;
    }
  },

  // Add operations
  getAddsByLaboratory: async (laboratoryId) => {
    try {
      const { data } = await client.query({
        query: GET_ADDS_BY_LABORATORY,
        variables: { laboratoryId },
        fetchPolicy: 'network-only'
      });
      return data.addsByLaboratory;
    } catch (error) {
      console.error(`Get adds by laboratory ${laboratoryId} error:`, error);
      throw error;
    }
  },

  createAdd: async (addData) => {
    try {
      const { data } = await client.mutate({
        mutation: CREATE_ADD,
        variables: { createAddInput: addData }
      });
      return data.createAdd;
    } catch (error) {
      console.error('Create add error:', error);
      throw error;
    }
  },

  updateAdd: async (addData) => {
    try {
      const { data } = await client.mutate({
        mutation: UPDATE_ADD,
        variables: { updateAddInput: addData }
      });
      return data.updateAdd;
    } catch (error) {
      console.error(`Update add ${addData.id} error:`, error);
      throw error;
    }
  },

  removeAdd: async (id) => {
    try {
      const { data } = await client.mutate({
        mutation: DELETE_ADD,
        variables: { id }
      });
      return data.removeAdd;
    } catch (error) {
      console.error(`Remove add ${id} error:`, error);
      throw error;
    }
  },

  // User operations
  getUsersByType: async (userType) => {
    try {
      const { data } = await client.query({
        query: GET_USERS_BY_TYPE,
        variables: { userType },
        fetchPolicy: 'network-only'
      });
      return data.usersByType;
    } catch (error) {
      console.error(`Get users by type ${userType} error:`, error);
      throw error;
    }
  },

  createUser: async (userData) => {
    try {
      const { data } = await client.mutate({
        mutation: CREATE_USER,
        variables: {
          email: userData.email,
          password: userData.password,
          userType: userData.userType || "LABORATORY",
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          tel: userData.tel || ''
        }
      });
      return data.createUser;
    } catch (error) {
      console.error('Create user error:', error);
      throw error;
    }
  },

  updateUser: async (userData) => {
    try {
      const { data } = await client.mutate({
        mutation: UPDATE_USER,
        variables: { updateUserInput: userData }
      });
      return data.updateUser;
    } catch (error) {
      console.error(`Update user ${userData.id} error:`, error);
      throw error;
    }
  },

  removeUser: async (id) => {
    try {
      const { data } = await client.mutate({
        mutation: DELETE_USER,
        variables: { id }
      });
      return data.removeUser;
    } catch (error) {
      console.error(`Remove user ${id} error:`, error);
      throw error;
    }
  },

  associateUserWithLaboratory: async (userId, laboratoryId) => {
    try {
      const { data } = await client.mutate({
        mutation: ASSOCIATE_USER_WITH_LABORATORY,
        variables: { userId, laboratoryId }
      });
      return data.associateUserToLaboratory;
    } catch (error) {
      console.error(`Associate user ${userId} with laboratory ${laboratoryId} error:`, error);
      throw error;
    }
  },

  // Label operations
  getPromotionLabels: async (promotionId) => {
    try {
      const { data } = await client.query({
        query: GET_PROMOTION_LABELS,
        variables: { promotionId },
        fetchPolicy: 'network-only'
      });
      return data.labelsByPromotion;
    } catch (error) {
      console.error(`Get promotion labels ${promotionId} error:`, error);
      throw error;
    }
  },

  getLabelsStatistics: async (promotionId) => {
    try {
      const { data } = await client.query({
        query: GET_LABELS_STATISTICS,
        variables: { promotionId },
        fetchPolicy: 'network-only'
      });
      return data.labelsStatistics;
    } catch (error) {
      console.error(`Get labels statistics ${promotionId} error:`, error);
      throw error;
    }
  },

  getLabelsCountByPromotion: async (promotionId) => {
    try {
      const { data } = await client.query({
        query: GET_LABELS_COUNT_BY_PROMOTION,
        variables: { promotionId },
        fetchPolicy: 'network-only'
      });
      return data.labelsCountByPromotion;
    } catch (error) {
      console.error(`Get labels count by promotion ${promotionId} error:`, error);
      throw error;
    }
  },

  generateQRCodes: async (promotionId, quantity) => {
    try {
      const { data } = await client.mutate({
        mutation: GENERATE_QR_CODES,
        variables: { promotionId, quantity }
      });
      return data.generateQRCodes;
    } catch (error) {
      console.error(`Generate QR codes for promotion ${promotionId} error:`, error);
      throw error;
    }
  },

  // Staff management
  addStaffToLaboratory: async (laboratoryId, userId) => {
    try {
      const { data } = await client.mutate({
        mutation: ADD_STAFF_TO_LABORATORY,
        variables: { laboratoryId, userId }
      });
      return data.addStaffToLaboratory;
    } catch (error) {
      console.error(`Add staff to laboratory ${laboratoryId} error:`, error);
      throw error;
    }
  },

  removeStaffFromLaboratory: async (laboratoryId, userId) => {
    try {
      const { data } = await client.mutate({
        mutation: REMOVE_STAFF_FROM_LABORATORY,
        variables: { laboratoryId, userId }
      });
      return data.removeStaffFromLaboratory;
    } catch (error) {
      console.error(`Remove staff from laboratory ${laboratoryId} error:`, error);
      throw error;
    }
  }
};

// ================================
// PRODUCT SUBCATEGORIES
// ================================

export const GET_PRODUCT_SUBCATEGORIES = gql`
  query GetProductSubcategories {
    productSubcategories {
      id
      name
      category
    }
  }
`;

// ================================
// EXPORT DEFAULT
// ================================

export default laboratoryApi;